import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, Divider, Typography, IconButton, CircularProgress, Alert, Drawer, List, ListItemButton, ListItemText,Snackbar } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate } from "react-router-dom";
import AddToCartButton from "../Components/AddToCartButton";
import getBrand from "../brands/getBrand";
import { useFetchCart, useGetProducts, useGetInventory } from "../Components/globalFunctions";
import { Delivery_timeslots } from "../Components/user_cart/delivery_timeslots";
import CustomAxios from "../utils/CustomAxios";
import { BillSection } from "../Components/user_cart/bill_section";
import { updateUserCart, updateCartId, updateId } from "../Redux/user";
import { openRazorpayCheckout } from "../utils/razorpayHelper";
import { openCashfreeCheckout } from "../utils/cashfreeHelper";
import { useHubServiceable } from "../hooks/useHubServiceable";
// eslint-disable-next-line no-unused-vars -- serviceable logic lives in useHubServiceable; imports kept for no-undef if refs exist
import { distanceKm, pointInPolygon } from "../utils/hubServiceableUtils";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

const CartPage = () => {
  const brand = getBrand();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userCart = useSelector((state) => state.user.userCart || []);
  const cart_id = useSelector((state) => state.user.cart_id || '');
  const products = useSelector((state) => state.products.products || []);
  const userId = useSelector((state) => state.user.id);
  const userMobileno = useSelector((state) => state.user.mobileno);
  const selectedHub = useSelector((state) => state.user.selected_hub);
  const inventory = useSelector((state) => state.inventory.inventory || []);
  const hubIdFromStorage = localStorage.getItem('selected_hub');
  const hubId = hubIdFromStorage;
  const fetchCart = useFetchCart();
  const getProducts = useGetProducts();
  const getInventory = useGetInventory();
  const [expandedRows, setExpandedRows] = useState({});
  const [hubAddressExpanded, setHubAddressExpanded] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);
  const [deliveryType, setDeliveryType] = useState(() => {
    const stored = localStorage.getItem("cart_delivery_type");
    return stored === "schedule" || stored === "pickup" ? stored : "pickup";
  });
  const [selectedSlot, setSelectedSlot] = useState(() => {
    try {
      const raw = localStorage.getItem("cart_selected_slot");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [selectedDay, setSelectedDay] = useState(() => {
    const raw = localStorage.getItem("cart_selected_day");
    return raw || null;
  });
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentGateway] = useState('cashfree'); // Default to Cashfree
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [bulkRemoveDialog, setBulkRemoveDialog] = useState(false);
  const [notAvailableProductIds, setNotAvailableProductIds] = useState([]);
  const [stockToastOpen, setStockToastOpen] = useState(false);

  // Ensure userId is hydrated on direct /cart refresh (similar to home.js)
  useEffect(() => {
    if (!userId) {
      const storedId = localStorage.getItem('id');
      if (storedId) {
        // console.log("[CartPage] Hydrating userId from localStorage", storedId);
        dispatch(updateId(storedId));
      }
    }
  }, [userId, dispatch]);
  useEffect(() => {
    if (!userId) {
      setCartLoading(false);
      return;
    }

    // Prefer hub from Redux, but fall back to localStorage on page refresh
    let effectiveHubId = null;
    if (selectedHub) {
      effectiveHubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
    } else if (hubIdFromStorage) {
      try {
        const parsed = JSON.parse(hubIdFromStorage);
        effectiveHubId = Array.isArray(parsed) ? parsed[0] : parsed;
      } catch (e) {
        effectiveHubId = hubIdFromStorage;
      }
    }

    if (!effectiveHubId) {
      setCartLoading(false);
      return;
    }

    setCartLoading(true);
    
    fetchCart(userId, effectiveHubId, "online")
      .finally(() => {
        setCartLoading(false);
      });
  }, [userId, selectedHub, hubIdFromStorage]);

  useEffect(() => {
    getProducts();
  }, []);

  // Load inventory on mount/refresh so selectedInventory and serviceability work (pincode/radius) even when landing directly on cart
  useEffect(() => {
    getInventory();
  }, []);

  // Persist delivery type across navigation
  useEffect(() => {
    localStorage.setItem("cart_delivery_type", deliveryType);
  }, [deliveryType]);

  // Persist selected delivery slot and day across navigation
  useEffect(() => {
    if (selectedSlot) {
      try {
        localStorage.setItem("cart_selected_slot", JSON.stringify(selectedSlot));
      } catch {
        // ignore JSON errors
      }
    } else {
      localStorage.removeItem("cart_selected_slot");
    }
  }, [selectedSlot]);

  useEffect(() => {
    if (selectedDay) {
      localStorage.setItem("cart_selected_day", selectedDay);
    } else {
      localStorage.removeItem("cart_selected_day");
    }
  }, [selectedDay]);

  // Load delivery addresses when scheduling delivery
  useEffect(() => {
    if (!userId) return;
    if (deliveryType !== "schedule") return;

    const effectiveUserId = userId || localStorage.getItem("id");
    if (!effectiveUserId) return;

    setAddressLoading(true);
    CustomAxios.get(`address/${effectiveUserId}`)
      .then((response) => {
        const list = Array.isArray(response.data) ? response.data : [];
        setDeliveryAddresses(list);

        let preferredId = localStorage.getItem("cart_selected_address_id");
        let selected = null;
        if (preferredId && list.length > 0) {
          selected = list.find((addr) => String(addr.id) === preferredId) || null;
        }
        if (!selected && list.length > 0) {
          selected = list[0];
        }
        if (preferredId) {
          localStorage.removeItem("cart_selected_address_id");
        }
        setSelectedDeliveryAddress(selected);
      })
      .catch((err) => {
        console.error("Error checking delivery addresses:", err);
        setDeliveryAddresses([]);
        setSelectedDeliveryAddress(null);
      })
      .finally(() => setAddressLoading(false));
  }, [userId, deliveryType]);

  const totals = useMemo(() => {
  let items = 0;
  let subTotal = 0;
  let taxTotal = 0;
  let TransactionFee = 0;
  let weightedTaxSum = 0; 
  let totalPriceQty = 0; 

  (userCart || []).forEach((item) => {
    (item.capacity || []).forEach((cap) => {
      const qty = Number(cap.item_quantity || 0);
      if (qty <= 0) return;

      const price =
        Number(cap.item_offers_price) > 0
          ? Number(cap.item_offers_price)
          : Number(cap.item_selling_price || cap.item_price || 0);

      const taxPercentage = Number(cap.item_tax || 0);
      
      // Calculate tax amount: (price * tax_percentage / 100) * quantity
      const itemTaxAmount = (price * taxPercentage / 100) * qty;

      items += qty;
      subTotal += qty * price;
      taxTotal += itemTaxAmount;
      
      // For weighted average tax percentage calculation
      weightedTaxSum += price * qty * taxPercentage;
      totalPriceQty += price * qty;
    });
  });
  TransactionFee = Number(
    ((Number(subTotal.toFixed(2)) + Number(taxTotal.toFixed(2))) * 0.029).toFixed(2)
  );  // Calculate weighted average tax percentage
  const averageTaxPercentage = totalPriceQty > 0 
    ? Number((weightedTaxSum / totalPriceQty).toFixed(2))
    : 0;

  const result = {
    items,
    subTotal: Number(subTotal.toFixed(2)),
    taxTotal: Number(taxTotal.toFixed(2)),
    TransactionFee,
    grandTotal: Number(subTotal.toFixed(2)) + Number(taxTotal.toFixed(2)) + TransactionFee,
    taxPercentage: averageTaxPercentage,
  };
  
  // // Debug: totals recomputed whenever cart changes
  // console.log("[CartPage] Recomputed totals", {
  //   items: result.items,
  //   subTotal: result.subTotal,
  //   taxTotal: result.taxTotal,
  //   grandTotal: result.grandTotal,
  // });

  return result;
}, [userCart]);

  const isCartEmpty = !cartLoading && totals.items === 0;

  // // Extra debug: log when cart changes (useful after page refresh)
  // useEffect(() => {
  //   console.log("[CartPage] Cart changed (effect)", {
  //     userId,
  //     selectedHub,
  //     hubIdFromStorage,
  //     cartLength: (userCart || []).length,
  //     cartItems: userCart,
  //     totalsItems: totals.items,
  //   });
  // }, [userCart, userId, selectedHub, hubIdFromStorage, totals.items]);

  /**
   * Get hub name from Redux inventory state (similar to header)
   */
  const getHubName = (hubId) => {
    if (!hubId || !inventory || !Array.isArray(inventory)) return null;
    const hubIdNum = Array.isArray(hubId) ? hubId[0] : hubId;
    const matched = inventory.find((inv) => inv.value === hubIdNum || inv.value === parseInt(hubIdNum));
    return matched ? matched.name : null;
  };

  /**
   * Get hub inventory details from Redux inventory state
   */
  const getHubInventory = (hubId) => {
    if (!hubId || !inventory || !Array.isArray(inventory)) return null;
    const hubIdNum = Array.isArray(hubId) ? hubId[0] : hubId;
    const matched = inventory.find((inv) => inv.value === hubIdNum || inv.value === parseInt(hubIdNum));
    return matched || null;
  };

  // Resolve current hub id (for serviceable-area checks)
  const effectiveHubId = useMemo(() => {
    if (selectedHub) return Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
    try {
      const raw = hubIdFromStorage;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
      return hubIdFromStorage;
    }
  }, [selectedHub, hubIdFromStorage]);

  const selectedInventory = useMemo(
    () => getHubInventory(effectiveHubId),
    [inventory, effectiveHubId]
  );

  const { isAddressServiceable } = useHubServiceable(selectedInventory);

  // When hub/inventory or addresses change, ensure selected address is serviceable; otherwise pick first serviceable or clear
  useEffect(() => {
    if (deliveryType !== "schedule") return;
    setSelectedDeliveryAddress((prev) => {
      if (!prev) return prev;
      if (isAddressServiceable(prev)) return prev;
      const list = deliveryAddresses;
      const firstServiceable = (Array.isArray(list) ? list : []).find((addr) => isAddressServiceable(addr));
      return firstServiceable || null;
    });
  }, [deliveryType, selectedInventory, deliveryAddresses, isAddressServiceable]);

  const getFrontProductIdsForSubmit = () => {
    if (!userCart || userCart.length === 0) return [];
    return userCart
      .filter(item => {
        const capacities = item.capacity || [];
        return capacities.some(cap => (cap?.item_quantity || 0) > 0);
      })
      .map(item => item.product_id);
  };

  /**
   * Step 1: Start payment - Create payment order (Cashfree or Razorpay)
   */
  const SubmitAndPay = async () => {
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);

      const payload = {
        amount: totals.subTotal.toString(),
        transaction_fee: totals.TransactionFee.toString(),
        total_amount: totals.grandTotal.toString(),
        user: userId,
        cart_items: Array.isArray(cart_id) ? cart_id : [cart_id],
        invetory_id: hubId,
        front_product_ids: getFrontProductIdsForSubmit(),
      };

      // Route to appropriate payment endpoint based on gateway
      const paymentEndpoint = paymentGateway === 'cashfree' 
        ? 'start_payment/'  // Cashfree endpoint
        : 'start_payment_razor/';  // Razorpay endpoint

      const response = await CustomAxios.post(paymentEndpoint, payload, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('authtoken')}`,
        },
      });

      // Handle out-of-stock items (if any)
      if (response.data.removeable_ids?.length > 0) {
        setNotAvailableProductIds(response.data.removeable_ids);
        setBulkRemoveDialog(true);
        setCheckoutLoading(false);
        return;
      }

      const orderDtls = response.data;
      setOrderDetails(orderDtls);

      // Validate slot selection first
      if ((deliveryType === 'pickup' || deliveryType === 'schedule') && !selectedSlot) {
        const slotTypeText = deliveryType === 'pickup' ? 'pickup' : 'delivery';
        setCheckoutError(`Please select a ${slotTypeText} time slot before proceeding.`);
        setCheckoutLoading(false);
        return;
      }

      // For scheduled delivery, validate address is within hub service area
      if (deliveryType === 'schedule') {
        if (!selectedDeliveryAddress) {
          setCheckoutError('Please select a delivery address.');
          setCheckoutLoading(false);
          return;
        }
        if (!isAddressServiceable(selectedDeliveryAddress)) {
          setCheckoutError('Selected address is outside this hub\'s delivery area. Please choose an address within the service area.');
          setCheckoutLoading(false);
          return;
        }
      }

      // Route to appropriate payment flow based on gateway
      if (paymentGateway === 'cashfree') {
        // Cashfree flow: Check for payment_session_id
        if (
          orderDtls &&
          orderDtls.payment &&
          orderDtls.payment.payment_session_id
        ) {
          await processCashfreePayment(orderDtls);
        } else {
          setCheckoutError('Failed to initialize payment. Please try again.');
          setCheckoutLoading(false);
        }
      } else {
        // Razorpay flow: Check for payment.id
      if (
        orderDtls &&
        orderDtls.payment &&
        orderDtls.payment.id
      ) {
        await processPayment(orderDtls);
      } else {
        setCheckoutError('Failed to initialize payment. Please try again.');
        setCheckoutLoading(false);
        }
      }
    } catch (error) {
      setCheckoutLoading(false);
      console.error('Payment initialization error:', error);
      setCheckoutError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to start payment. Please try again.'
      );
    }
  };

  const removeBulkFromCartAPI = async (selectedProductIds) => {
    try {
      const payload = {
        product_ids: selectedProductIds,
        hub: Array.isArray(selectedHub) ? selectedHub[0] : selectedHub
      };

      await CustomAxios.post(
        `remove-bulk-from-cart/${userId}/`,
        payload
      );


      // Refresh cart
      const hubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
      await fetchCart(userId, hubId, "online");

    } catch (error) {
      console.error("Error removing products:", error);
    }
  };

  /**
   * Step 2: Process payment - Create checkout and open Razorpay
   */
  const processPayment = async (orderDetails) => {
    try {
      // Validate slot selection if needed
      if ((deliveryType === 'schedule' || deliveryType === 'pickup') && !selectedSlot) {
        const slotTypeText = deliveryType === 'pickup' ? 'pickup' : 'delivery';
        setCheckoutError(`Please select a ${slotTypeText} time slot`);
        setCheckoutLoading(false);
        return;
      }

      // Validate slot type matches delivery type
      if (selectedSlot && (deliveryType === 'schedule' || deliveryType === 'pickup')) {
        const expectedSlotType = deliveryType === 'pickup' ? 'pickup' : 'delivery';
        if (selectedSlot.slot_type !== expectedSlotType) {
          setCheckoutError(`Slot type mismatch. Selected slot is for ${selectedSlot.slot_type === 'pickup' ? 'Self Pickup' : 'Delivery'}, but order is for ${deliveryType === 'pickup' ? 'self pickup' : 'delivery'}. Please select a new slot.`);
          setCheckoutLoading(false);
          return;
        }
      }

      // Get hub inventory details to check self_pickup_waiver
      const effectiveHubId = selectedHub || hubIdFromStorage;
      const hubInventory = getHubInventory(effectiveHubId);
      
      // Calculate self-pickup offer values only if:
      // 1. Hub has self_pickup_waiver enabled
      // 2. Customer selected self-pickup (deliveryType === 'pickup')
      const isSelfPickup = deliveryType === 'pickup';
      const hasSelfPickupWaiver = hubInventory?.self_pickup_waiver === true;
      const shouldApplySelfPickupDiscount = isSelfPickup && hasSelfPickupWaiver;
      
      let selfPickupOfferPercentage = 0;
      let selfPickupOfferAmount = 0;
      
      if (shouldApplySelfPickupDiscount && hubInventory?.self_pickup_waiver_percentage) {
        selfPickupOfferPercentage = parseFloat(hubInventory.self_pickup_waiver_percentage) || 0;
        // Calculate discount amount based on subTotal (amount after wallet deduction would be more accurate, but we use subTotal here)
        // The actual calculation should match what's done in the cart calculation logic
        selfPickupOfferAmount = (totals.subTotal * selfPickupOfferPercentage) / 100;
      }

      // Build delivery address from selected address (for scheduled delivery)
      let deliveryAddressData = null;
      if (deliveryType === 'schedule' && selectedDeliveryAddress) {
        // Parse state_country to extract state (format: "State, Country" or just "State")
        const stateCountryParts = (selectedDeliveryAddress.state_country || "").split(",").map(s => s.trim());
        const state = stateCountryParts[0] || "";
        const country = stateCountryParts[1] || "";
        
        // Combine door_flat_no and address for address_line1
        const addressParts = [
          selectedDeliveryAddress.door_flat_no,
          selectedDeliveryAddress.address
        ].filter(Boolean);
        const addressLine1 = addressParts.join(", ") || selectedDeliveryAddress.address || "";
        
        // Extract city from address or use a default (you may need to adjust this based on your address format)
        // For now, we'll try to extract from the address string or use empty
        const city = ""; // You may want to parse this from the address field if it contains city info
        
        const effectiveMobileno = userMobileno || localStorage.getItem("mobileno") || "";
        deliveryAddressData = {
          name: selectedDeliveryAddress.name || "",
          address_line1: addressLine1,
          city: city,
          state: state,
          pincode: selectedDeliveryAddress.pin || "",
          mobileNo: effectiveMobileno
        };
      } else {
        // For pickup or when no address selected, use minimal/default address
        const effectiveMobileno = userMobileno || localStorage.getItem("mobileno") || "";
        deliveryAddressData = {
          name: "",
          address_line1: "",
          city: "",
          state: "",
          pincode: "",
          mobileNo: effectiveMobileno
        };
      }

      // Build checkout payload
      const checkoutPayload = {
        total_amount: totals.grandTotal.toString(),
        amount: totals.subTotal.toString(),
        orders: userCart,
        delivery_charge: 0,
        paymentmethod: 'razorpay',
        cart_items: Array.isArray(cart_id) ? cart_id : [cart_id],
        user_id: userId,
        orderDetais: orderDetails,
        offers_id: [],
        tax: totals.taxTotal.toString(),
        delivery_mode: deliveryType === 'pickup' ? 2 : 1,
        currency: 'INR',
        delivery_address: deliveryAddressData,
        inventory_Id: hubId,
        order_instructions: "Please deliver fast",
        self_pickup_waiver: hasSelfPickupWaiver || false,
        self_pickup_offer_percentage: selfPickupOfferPercentage,
        self_pickup_offer_amount: parseFloat(selfPickupOfferAmount.toFixed(2)),
        wallet_offer_percentage: 0,
        wallet_offer_amount: 0,
        // Delivery slot if scheduled
        delivery_slot: (deliveryType === 'schedule' || deliveryType === 'pickup') && selectedSlot
          ? {
              slot_id: selectedSlot.id,
              day_of_week: selectedDay,
              start_time: selectedSlot.start_time,
              end_time: selectedSlot.end_time,
            }
          : null,
      };

      // Call checkout API
      const checkoutResponse = await CustomAxios.post('checkout_razor/', checkoutPayload);

      if (checkoutResponse.data.checkout_id) {
        // Step 3: Open Razorpay checkout
        const razorpayOrderId = orderDetails.payment.id;

        // Get user details from localStorage
        const userEmail = localStorage.getItem('email') || '';

        try {
          const effectiveHubId = selectedHub || hubIdFromStorage;
          const hubName = getHubName(effectiveHubId) || brand.appShortName || 'SmartKart';
          
          // Start opening Razorpay modal
          const paymentPromise = openRazorpayCheckout({
            key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_S0YzVrOIBaLw7c',
            amount: Math.round(totals.grandTotal * 100), // Convert to paise
            currency: 'INR',
            order_id: razorpayOrderId,
            name: hubName,
            image: brand.appIcon || '',
            description: `Order Payment - ${checkoutResponse.data.checkout_id}`,
            prefill: {
              email: userEmail,
              contact: '',
            },
            theme: {
              color: brand?.theme?.primary || brand?.themeColor || '#1976d2',
            },
          });

          // Reset loading state immediately after modal opens
          // The modal opens synchronously when razorpay.open() is called
          setTimeout(() => {
            setCheckoutLoading(false);
          }, 100);

          const paymentResponse = await paymentPromise;
          console.log('raz',paymentResponse,razorpayOrderId)
          // Step 4: Payment successful - Save payment details
          await savePaymentDetails(paymentResponse, razorpayOrderId);
        } catch (razorpayError) {
          setCheckoutLoading(false);
          if (razorpayError.message === 'Payment cancelled by user') {
          } else {
            console.error('Razorpay error:', razorpayError);
            setCheckoutError('Payment failed. Please try again.');
          }
        }
      } else {
        setCheckoutLoading(false);
        setCheckoutError('Checkout failed. Please try again.');
      }
    } catch (error) {
      setCheckoutLoading(false);
      console.error('Payment processing error:', error);
      setCheckoutError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to process payment. Please try again.'
      );
    }
  };

  /**
   * Step 4: Save payment details after successful Razorpay payment
   */
  const savePaymentDetails = async (paymentResponse, razorpayOrderId) => {
    try {
      const saveResponse = await CustomAxios.post('save_payment_razor/', {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
      });

      // Verify payment was saved successfully
      if (
        saveResponse?.data?.payment_status &&
        saveResponse?.data?.signature &&
        saveResponse?.data?.payment_id
      ) {
        // Clear cart
        dispatch(updateUserCart([]));
        dispatch(updateCartId(''));
        setCheckoutLoading(false);

        // Navigate to payment success page
        navigate(`/payment-success/${razorpayOrderId}`);
      } else {
        setCheckoutLoading(false);
        setCheckoutError('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      setCheckoutLoading(false);
      console.error('Save payment error:', error);
      setCheckoutError('Failed to save payment details. Please contact support.');
    }
  };

  /**
   * Process Cashfree payment - Create checkout and open Cashfree modal
   */
  const processCashfreePayment = async (orderDetails) => {
    try {
      // Validate slot selection if needed
      if ((deliveryType === 'schedule' || deliveryType === 'pickup') && !selectedSlot) {
        const slotTypeText = deliveryType === 'pickup' ? 'pickup' : 'delivery';
        setCheckoutError(`Please select a ${slotTypeText} time slot`);
        setCheckoutLoading(false);
        return;
      }

      // Validate slot type matches delivery type
      if (selectedSlot && (deliveryType === 'schedule' || deliveryType === 'pickup')) {
        const expectedSlotType = deliveryType === 'pickup' ? 'pickup' : 'delivery';
        if (selectedSlot.slot_type !== expectedSlotType) {
          setCheckoutError(`Slot type mismatch. Selected slot is for ${selectedSlot.slot_type === 'pickup' ? 'Self Pickup' : 'Delivery'}, but order is for ${deliveryType === 'pickup' ? 'self pickup' : 'delivery'}. Please select a new slot.`);
          setCheckoutLoading(false);
          return;
        }
      }

      // Get hub inventory details
      const effectiveHubId = selectedHub || hubIdFromStorage;
      const hubInventory = getHubInventory(effectiveHubId);
      
      const isSelfPickup = deliveryType === 'pickup';
      const hasSelfPickupWaiver = hubInventory?.self_pickup_waiver === true;
      const shouldApplySelfPickupDiscount = isSelfPickup && hasSelfPickupWaiver;
      
      let selfPickupOfferPercentage = 0;
      let selfPickupOfferAmount = 0;
      
      if (shouldApplySelfPickupDiscount && hubInventory?.self_pickup_waiver_percentage) {
        selfPickupOfferPercentage = parseFloat(hubInventory.self_pickup_waiver_percentage) || 0;
        selfPickupOfferAmount = (totals.subTotal * selfPickupOfferPercentage) / 100;
      }

      // Build delivery address
      let deliveryAddressData = null;
      if (deliveryType === 'schedule' && selectedDeliveryAddress) {
        const stateCountryParts = (selectedDeliveryAddress.state_country || "").split(",").map(s => s.trim());
        const state = stateCountryParts[0] || "";
        const effectiveMobileno = userMobileno || localStorage.getItem("mobileno") || "";
        
        const addressParts = [
          selectedDeliveryAddress.door_flat_no,
          selectedDeliveryAddress.address
        ].filter(Boolean);
        const addressLine1 = addressParts.join(", ") || selectedDeliveryAddress.address || "";
        
        deliveryAddressData = {
          name: selectedDeliveryAddress.name || "",
          address_line1: addressLine1,
          city: "",
          state: state,
          pincode: selectedDeliveryAddress.pin || "",
          mobileNo: effectiveMobileno
        };
      } else {
        const effectiveMobileno = userMobileno || localStorage.getItem("mobileno") || "";
        deliveryAddressData = {
          name: "",
          address_line1: "",
          city: "",
          state: "",
          pincode: "",
          mobileNo: effectiveMobileno
        };
      }

      // Build checkout payload (use 'cashfree' as paymentmethod)
      const checkoutPayload = {
        total_amount: totals.grandTotal.toString(),
        amount: totals.subTotal.toString(),
        orders: userCart,
        delivery_charge: 0,
        paymentmethod: 'cashfree', // Important: Use 'cashfree' not 'razorpay'
        cart_items: Array.isArray(cart_id) ? cart_id : [cart_id],
        user_id: userId,
        orderDetais: orderDetails, // Contains order_id from Cashfree
        offers_id: [],
        tax: totals.taxTotal.toString(),
        delivery_mode: deliveryType === 'pickup' ? 2 : 1,
        currency: 'INR',
        delivery_address: deliveryAddressData,
        inventory_Id: hubId,
        order_instructions: "Please deliver fast",
        self_pickup_waiver: hasSelfPickupWaiver || false,
        self_pickup_offer_percentage: selfPickupOfferPercentage,
        self_pickup_offer_amount: parseFloat(selfPickupOfferAmount.toFixed(2)),
        wallet_offer_percentage: 0,
        wallet_offer_amount: 0,
        delivery_slot: (deliveryType === 'schedule' || deliveryType === 'pickup') && selectedSlot
          ? {
              slot_id: selectedSlot.id,
              day_of_week: selectedDay,
              start_time: selectedSlot.start_time,
              end_time: selectedSlot.end_time,
            }
          : null,
      };

      // Call checkout/ endpoint (not checkout_razor/)
      const checkoutResponse = await CustomAxios.post('checkout/', checkoutPayload);

      if (checkoutResponse.data.checkout_id) {
        // Get payment_session_id from orderDetails
        const paymentSessionId = orderDetails.payment.payment_session_id;
        const cashfreeOrderId = orderDetails.order_id || orderDetails.payment.order_id;

        if (!paymentSessionId) {
          setCheckoutLoading(false);
          setCheckoutError('Payment session ID not found. Please try again.');
          return;
        }

        // Store order_id for status checking
        sessionStorage.setItem('cashfree_order_id', cashfreeOrderId);
        sessionStorage.setItem('cashfree_checkout_id', checkoutResponse.data.checkout_id);

        try {
          // Reset loading state before opening modal
          setTimeout(() => {
            setCheckoutLoading(false);
          }, 100);

          // Open Cashfree checkout modal
          const paymentResponse = await openCashfreeCheckout(paymentSessionId);
          
          console.log('Cashfree payment response:', paymentResponse);
          
          // After payment modal closes, check payment status
          // Webhook handles verification asynchronously, so we poll the status
          await checkCashfreePaymentStatus(cashfreeOrderId, checkoutResponse.data.checkout_id);
          
        } catch (cashfreeError) {
          setCheckoutLoading(false);
          if (cashfreeError.message === 'Payment cancelled or failed') {
            // User cancelled - show message
            setCheckoutError('Payment was cancelled. Please try again when ready.');
          } else {
            console.error('Cashfree error:', cashfreeError);
            setCheckoutError('Payment failed. Please try again.');
          }
        }
      } else {
        setCheckoutLoading(false);
        setCheckoutError('Checkout failed. Please try again.');
      }
    } catch (error) {
      setCheckoutLoading(false);
      console.error('Cashfree payment processing error:', error);
      setCheckoutError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to process payment. Please try again.'
      );
    }
  };

  /**
   * Check Cashfree payment status (polls until payment is verified)
   */
  const checkCashfreePaymentStatus = async (orderId, checkoutId) => {
    try {
      setCheckoutLoading(true);
      
      // Wait a moment for webhook to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Poll payment status (max 5 attempts, 2 seconds apart)
      let attempts = 0;
      const maxAttempts = 5;
      let paymentVerified = false;
      
      while (attempts < maxAttempts && !paymentVerified) {
        try {
          // Call check_payment_status endpoint
          const statusResponse = await CustomAxios.get(`check_payment_status/${orderId}/${userId}/`);
          
          // Check if payment_status is true (set by webhook)
          if (statusResponse?.data?.payment_status === true) {
            paymentVerified = true;
            
            // Payment successful - clear cart and navigate
            dispatch(updateUserCart([]));
            dispatch(updateCartId(''));
            setCheckoutLoading(false);
            sessionStorage.removeItem('cashfree_order_id');
            sessionStorage.removeItem('cashfree_checkout_id');
            
            // Navigate to payment success page
            navigate(`/payment-success/${orderId}`);
            return;
          }
          
          // If not verified yet, wait and retry
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (statusError) {
          console.error('Error checking payment status:', statusError);
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }
      
      // If payment not verified after max attempts, still navigate (webhook will handle it)
      if (!paymentVerified) {
        setCheckoutLoading(false);
        // Still navigate - webhook will update status asynchronously
        navigate(`/payment-success/${orderId}`);
      }
      
    } catch (error) {
      console.error('Error in payment status check:', error);
      setCheckoutLoading(false);
      // Navigate anyway - webhook will handle verification
      navigate(`/payment-success/${orderId}`);
    }
  };

  /**
   * Main checkout handler - calls SubmitAndPay
   */
  const handleCheckout = () => {
    if (!cart_id || totals.items === 0) {
      setCheckoutError('Cart is empty');
      return;
    }
    SubmitAndPay();
  };
  console.log("-------userMobileno----->>>>>",userMobileno)

  const renderLineItems = () => {
    // Show loading state while fetching
    if (cartLoading) {
      return (
        <Box sx={{ padding: "32px", textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, fontSize: "14px", color: "#6B7280" }}>
            Loading cart...
          </Typography>
        </Box>
      );
    }

    if (!userCart || userCart.length === 0) {
      return (
        <Box sx={{ padding: "32px", textAlign: "center" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#6B7280" }}>
            Your cart is empty
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2, textTransform: "none", background: brand?.theme?.primary || "#1976d2" }}
            onClick={() => navigate("/home/dashboard")}
          >
            Browse products
          </Button>
        </Box>
      );
    }

    return userCart.map((item) =>
      (item.capacity || []).map((cap) => {
        
        const qty = Number(cap.item_quantity || 0);
        if (qty <= 0) return null;
        const unitPrice =
          Number(cap.item_offers_price) > 0
            ? Number(cap.item_offers_price)
            : Number(cap.item_selling_price || cap.item_price || 0);
        const lineTotal = unitPrice * qty;
        const productFromStore = products.find((p) => p.id === item.product_id);
      const rawStock =
        productFromStore?.available_units ??
        productFromStore?.total_units ??
        productFromStore?.inventory_details?.available_units ??
        0;

      const stockUnit =
        productFromStore?.unit ||
        productFromStore?.inventory_details?.unit ||
        "kg";


        const capacityValue = parseFloat(cap.item_capacity) || 0;
        const unit = cap.unit || "kg";

        // convert to base unit (same logic as home)
        const convertToBaseUnit = (value, unit) => {
          const numeric = parseFloat(value) || 0;
          const u = unit?.toLowerCase();

          if (u?.includes("kg")) return numeric * 1000;
          if (u?.includes("g")) return numeric;
          if (u?.includes("litre") || u?.includes("ltr") || u === "l") return numeric * 1000;
          if (u?.includes("ml")) return numeric;
          return numeric;
        };

        const totalStock = convertToBaseUnit(rawStock, stockUnit);

        // Calculate total used stock of this product across entire cart
        const totalUsedStockAcrossCart = (userCart || [])
          .filter(cartItem => cartItem.product_id === item.product_id)
          .reduce((total, cartItem) => {
            return total + (cartItem.capacity || []).reduce((capTotal, cartCap) => {
              const capQty = Number(cartCap.item_quantity || 0);
              const capValue = parseFloat(cartCap.item_capacity) || 0;
              const capUnit = cartCap.unit || productFromStore?.unit || "kg";

              return capTotal + (convertToBaseUnit(capValue, capUnit) * capQty);
            }, 0);
          }, 0);

        const remainingStock = totalStock - totalUsedStockAcrossCart;

        const isOutOfStockByWeight =
          remainingStock < convertToBaseUnit(capacityValue, unit);

        const productDetails = item.product || productFromStore || {};
        const imageSrc =
          (productDetails.image_urls && productDetails.image_urls[0]) ||
          (productDetails.images && productDetails.images[0]) ||
          productDetails.image ||
          productDetails.item_image ||
          cap?.image ||
          cap?.item_image ||
          "";
        const title = productDetails.item_name || item.item_name || "Item";
        const rowKey = `${item.product_id}-${cap.item_capacity}`;
        const isExpanded = !!expandedRows[rowKey];

        return (
          <Box
            key={rowKey}
            sx={{
              display: "flex",
              padding: "10px",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
              
            }}
            >
            <Box sx={{
              display: "flex",
              gap: "12px",
                alignItems: "center",
                    width: "100%",
            }}>     
            <Box
              component="img"
              src={imageSrc}
              alt={title}
              onError={(e) => {
                e.currentTarget.src = brand?.placeholderImage || "";
              }}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                objectFit: "cover",
                background: "#F3F4F6",
              }}
            />
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#111827" }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: "15px", fontWeight: 600, color: brand?.themeColor || "#1976d2" }}>
                ₹{lineTotal.toFixed(2)}
              </Typography>
                </Box>
                <IconButton
                onClick={() =>
                  setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                }
                size="small"
                sx={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <AddToCartButton
                productId={item.product_id}
                inventoryId={Array.isArray(selectedHub) ? selectedHub[0] : selectedHub}
                inventoryDetailsId={cap.inventory_details_id}
                selectedCapacity={cap}
                totalAmount={unitPrice}
                quantities={qty}
                disabled={false}
                disableIncrement={isOutOfStockByWeight}
                onStockLimit={() => {
                  setStockToastOpen(true);
                  setTimeout(() => setStockToastOpen(false), 2000);
                }}
                onUpdateCart={() => {
                  if (userId) {
                    const hubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
                    fetchCart(userId, hubId, "online");
                  }
                }}
              />
              
            </Box>
                </Box>
            {isExpanded && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                            mt: 1,
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                  Capacity: {cap.item_capacity}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                  Qty: {qty} @ ₹{unitPrice.toFixed(2)} = ₹{lineTotal.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Snackbar
              open={stockToastOpen}
              autoHideDuration={2000}
              onClose={() => setStockToastOpen(false)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ top: "60px" }}
            >
              <Box
                sx={{
                  backgroundColor: "#111",
                  color: "#fff",
                  px: 3,
                  py: 1.5,
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: 500,
                  boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                }}
              >
                That's all we have in stock
              </Box>
            </Snackbar>
            </Box>
        );
      })
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: brand?.backgroundColor || "#F9FAFB",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: { xs: 2, md: 3 },
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          height: { xs: "56px", md: "72px" },
        }}
      >
        <Button
          startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px",color: brand?.theme?.textPrimary || "#111827" }}/>}
          onClick={() => navigate("/home/user_management")}
          sx={{ textTransform: "none" }}
        >
          
        </Button>
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>
          Cart
        </Typography>
        <Box />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, flex: 1,display:"flex",flexDirection:"column",gap:'15px' }}>
        {checkoutError && (
          <Alert 
            severity="error" 
            onClose={() => setCheckoutError(null)}
            sx={{ mb: 1 }}
          >
            {checkoutError}
          </Alert>
        )}
        {cartLoading ? (
          <Box sx={{ padding: "32px", textAlign: "center", backgroundColor: "#fff", borderRadius: "8px" }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, fontSize: "14px", color: "#6B7280" }}>
              Loading cart...
            </Typography>
          </Box>
        ) : isCartEmpty ? (
          <Box sx={{ padding: "32px", textAlign: "center", backgroundColor: "#fff", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#6B7280" }}>
              Your cart is empty
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2, textTransform: "none", background: brand?.theme?.primary || brand?.themeColor || "#1976d2" }}
              onClick={() => navigate("/home/dashboard")}
            >
              Browse products
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ display:"flex",flexDirection:"column",backgroundColor:"#fff",borderRadius:"8px"}}>  
              <Typography sx={{ fontWeight: 600, fontSize: "14px",px:'10px',paddingTop:'10px'}}>
                Items ({totals.items})
              </Typography>
              {renderLineItems()}
            </Box>
            <Delivery_timeslots 
              deliveryType={deliveryType} 
              setDeliveryType={setDeliveryType}
              selectedSlot={selectedSlot}
              setSelectedSlot={setSelectedSlot}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
            />
            <BillSection
              itemTotal={totals.subTotal}
              deliveryFee={0}
              discount={0}
              tax={totals.taxTotal}
              TransactionFee={totals.TransactionFee}
              taxPercentage={totals.taxPercentage}
              grandTotal={totals.grandTotal}
            />
          </>
        )}

      </Box>

      {!cartLoading && !isCartEmpty && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid #E5E7EB",
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: { xs: "flex-start", md: "space-between" },
            gap: { xs: 1.5, md: 2 },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: { xs: "100%", md: "auto" } }}>
            {deliveryType === "schedule" && selectedDeliveryAddress && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  maxWidth: { xs: "100%", md: "360px" },
                }}
              >
                {(() => {
                  const type = (selectedDeliveryAddress.address_type || "").toLowerCase();
                  if (type === "home") {
                    return <HomeIcon sx={{ fontSize: 20, color: "#4B5563" }} />;
                  }
                  if (type === "office" || type === "work") {
                    return <BusinessIcon sx={{ fontSize: 20, color: "#4B5563" }} />;
                  }
                  return <LocationOnIcon sx={{ fontSize: 20, color: "#4B5563" }} />;
                })()}
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {selectedDeliveryAddress.address_type || "Address"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#4B5563",
                    ml: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {" - "}
                  {[
                    selectedDeliveryAddress.address,
                    selectedDeliveryAddress.state_country,
                    selectedDeliveryAddress.pin,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setAddressDrawerOpen(true)}
                  sx={{ ml: 0.5 }}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Hub Address for Self Pickup */}
            {deliveryType === "pickup" && selectedInventory?.pincodes?.[0]?.hub_address && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  maxWidth: { xs: "100%", md: "360px" },
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => setHubAddressExpanded(!hubAddressExpanded)}
                >
                  <BusinessIcon sx={{ fontSize: 20, color: "#4B5563" }} />
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    Pickup Location
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#4B5563",
                      flex: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {" - "}
                    {selectedInventory.pincodes[0].hub_address}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{
                      transform: hubAddressExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      p: 0.5,
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </Box>

                {hubAddressExpanded && (
                  <Box
                    sx={{
                      paddingLeft: "28px", // Align with text after icon
                      paddingTop: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#6B7280",
                        lineHeight: 1.4,
                      }}
                    >
                      {selectedInventory.pincodes[0].hub_address}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

          </Box>
          {deliveryType === "schedule" && !selectedDeliveryAddress ? (
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                background: brand?.themeColor || "#1976d2",
                minWidth: { xs: "100%", md: "180px" },
              }}
              onClick={() => navigate("/add-address?from=cart")}
              disabled={addressLoading}
            >
              {addressLoading ? "Checking address..." : "Add delivery address"}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                checkoutLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <ShoppingCartCheckoutIcon />
                )
              }
              sx={{
                textTransform: "none",
                background: brand?.themeColor || "#1976d2",
                minWidth: { xs: "100%", md: "180px" },
              }}
              onClick={handleCheckout}
              disabled={totals.items === 0 || checkoutLoading}
            >
             {checkoutLoading ? "Processing..." : `Checkout ₹${Number(totals.grandTotal).toFixed(2)}`}
            </Button>
          )}
        </Box>
      )}
      {/* Address switch drawer */}
      <Drawer
        anchor="bottom"
        open={addressDrawerOpen}
        onClose={() => setAddressDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "70vh",
          },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 999,
              backgroundColor: "#E5E7EB",
              mx: "auto",
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            Select delivery address
          </Typography>
          <List dense>
            {deliveryAddresses.map((addr) => {
              const serviceable = isAddressServiceable(addr);
              return (
                <ListItemButton
                  key={addr.id}
                  selected={serviceable && selectedDeliveryAddress && addr.id === selectedDeliveryAddress.id}
                  disabled={!serviceable}
                  onClick={() => {
                    if (!serviceable) return;
                    setSelectedDeliveryAddress(addr);
                    setAddressDrawerOpen(false);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    opacity: serviceable ? 1 : 0.7,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {addr.address_type || "Address"}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: serviceable ? "#4B5563" : "#9CA3AF",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {[addr.address, addr.state_country, addr.pin].filter(Boolean).join(", ")}
                        </Typography>
                        {!serviceable && (
                          <Typography sx={{ fontSize: 12, color: "#EF4444", mt: 0.25 }}>
                            Outside delivery area
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 1.5, textTransform: "none",backgroundColor: brand?.themeColor || "#1976d2",color: "#fff" ,borderColor: brand?.themeColor || "#1976d2"}}
            onClick={() => {
              setAddressDrawerOpen(false);
              navigate("/add-address?from=cart");
            }}
          >
            Add new address
          </Button>
        </Box>
      </Drawer>
      <Dialog
        open={bulkRemoveDialog}
        onClose={() => setBulkRemoveDialog(false)}
      >
        <DialogTitle>
          Unavailable Items
        </DialogTitle>

        <DialogContent>
          <Typography>
            Some items in your cart are unavailable. They must be removed before checkout.
            Would you like to remove them?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setBulkRemoveDialog(false)}
            color="inherit"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ backgroundColor: brand?.themeColor || "#1976d2" }}
            onClick={async () => {
              await removeBulkFromCartAPI(notAvailableProductIds);
              setBulkRemoveDialog(false);
              SubmitAndPay();
            }}
          >
            Yes, Remove Items
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CartPage;

